import { CommonModule, JsonPipe } from '@angular/common';
import { Component, computed, effect, input, OnDestroy, OnInit, Optional, Signal, signal } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideUploadCloud, lucideNetwork, lucideCircleCheck } from '@ng-icons/lucide';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MaterialModule } from 'src/app/shared/modules/material.module';
import { PageEvent } from '@angular/material/paginator';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ImportWizardService } from '../../../import-wizard.service';
import { ImportWizardStorage, TabularColumns, typeOptions, ValidationErrorsInterface } from '../../../import-wizard.storage';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ImportWizardComponent } from '../../../import-wizard.component';
import { ImportWizardGeoSpatialService } from '../import-wizard-geospatial.service';
import { FormsModule } from '@angular/forms';
import { AlertComponent } from 'src/app/shared/alert/alert.component';
import { ToastrService } from 'ngx-toastr';
import { NatService } from 'src/app/services/nat.service';
import { BehaviorSubject, delay, firstValueFrom } from 'rxjs';
import { Subscription as NatsSubscription } from 'nats.ws';
import { MapService } from 'src/app/services/map.service';
import { RepositoryService } from 'src/app/pages/project/map/repository/repository.service';
import { ProjectService } from 'src/app/services/projects.service';
import { MapSideBarService } from 'src/app/pages/project/map/map-sidebar/map-sidebar.service';
import { AddLayerToMapInterface } from 'src/app/interface/geobi/layer.interface';
import { DatasetType, GetImportWizardErrorsItem, GetImportWizardResponse, ImportWizardFile } from 'src/app/dtos/import-wizard/import-wizard.dto';
import { ImportWizardPendingStorage } from '../../pending-list/import-wizard-pending-list.storage';
import { ImportWizardPendingService } from '../../pending-list/import-wizard-pending-list.service';
import { ImportWizardGeoSpatialMiniMapComponent } from '../_components/minimap/import-wizard-geospatial-minimap.component';
import { StatusPageComponent } from 'src/app/shared/status-page/status-page.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedImportsStorage } from 'src/app/pages/shared-imports/shared-imports.storage';

export interface NatsImportWizardProgressResponseInterface { 
    status: string;
    rows_inserted_batch?: number;
    cumulativa_total?: number;
    total_expected?: number;
    progress_percentage?: number;
    phase?: 'upload' | 'rail' | 'done';
    message?: string;
    error?: string;
}

@Component({
    selector: 'app-import-wizard-geospatial-confirmation_validation',
    templateUrl: './import-wizard-geospatial-confirmation_validation.component.html',
    styleUrls: ['./import-wizard-geospatial-confirmation_validation.component.scss'],
    imports: [
        CommonModule,
        MaterialModule,
        ScrollingModule,
        FormsModule,
        AlertComponent,
        ImportWizardGeoSpatialMiniMapComponent,
        StatusPageComponent,
        NgIcon
    ],
    providers: [
        provideIcons({ lucideUploadCloud, lucideNetwork, lucideCircleCheck })
    ],
    standalone: true
})
export class ImportWizardGeoSpatialConfirmationValidationComponent implements OnInit, OnDestroy {
    file = input<File>();

    private progressSubscription?: NatsSubscription;

    currentImportJob = signal<GetImportWizardResponse | null>(null);
    importJobFiles = computed(() => this.currentImportJob()?.files);

    pagesSizes = { 
        'min': 5,
        'mid-lower': 10,
        'mid-upper': 25,
        'max': 100
    }
    
    tabularRows = computed(() => this.importWizardStorage.rows());
    tabularTableDataSource = computed(() => {
        const start = (this.currentPage() - 1) * this.pageSize();
        const end = start + this.pageSize();

        return this.tabularRows().slice(start, end);
    });

    serviceColumns = computed(() => this.importWizardStorage.columns());
    serviceColumnsNames = computed(() => this.serviceColumns().map((col) => col.name));

    displayColumns = computed(() => this.importWizardStorage.displayColumns());
    displayColumnsNames = computed(() => this.importWizardStorage.display_columns_names());

    pageSize = signal(this.pagesSizes['mid-lower']);
    currentPage = signal(1);
    total = computed(() => this.importWizardStorage.validationErrors().total);

    _state = computed(() => this.importWizardStorage.state());

    groupedErrors = computed(() => {
        const map = new Map<string, any>();

        this.importWizardStorage.validationErrors().items.forEach(err => {
            if (!map.has(err.column_name)) {
                map.set(err.column_name, {
                    column_name: err.original_column,
                    // expected_type: err.expected_type,
                    rows: [],
                    values: [],
                    is_invalid_file: err.is_invalid_file,
                    error_message: err.error_message
                });
            }

            const group = map.get(err.column_name);
            group.rows.push(err.row_number);
            group.values.push(err.value);
        });

        return Array.from(map.values());
    });

    showErrorsSectionTitle = computed(() =>
        this.groupedErrors().length > 0 &&
        !this.groupedErrors().some(e => e.is_invalid_file)
    );

    showDetailedErrors = signal<boolean>(false);

    isMultifeature = computed(() => this.importWizardStorage.state().MultiFeatured);

    toggleDetailedErrors() {
        this.showDetailedErrors.set(!this.showDetailedErrors());
    }

    private createRepositoryLoadingProgress$ = new BehaviorSubject<number>(0.00);
    createRepositoryLoadingProgress = this.createRepositoryLoadingProgress$.asObservable();

    private progressInterval: ReturnType<typeof setInterval> | null = null;

    private startProgressSimulation(startValue: number, ceiling: number): void {
        this.stopProgressSimulation();
        this.createRepositoryLoadingProgress$.next(startValue);

        this.progressInterval = setInterval(() => {
            const current = this.createRepositoryLoadingProgress$.getValue();
            if (current < ceiling) {
                const step = (ceiling - current) * 0.0072;
                const next = current + Math.max(step, 0.01);
                this.createRepositoryLoadingProgress$.next(Math.min(next, ceiling - 0.1));
            } else {
                this.stopProgressSimulation();
            }
        }, 1000);
    }

    private stopProgressSimulation(): void {
        if (this.progressInterval !== null) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
    }

    progressMessage = signal<string>('');
    progressPhase = signal<'upload' | 'rail' | 'done' | ''>('');
    errorMessage = signal<string | null>(null);
    successMessage = signal<string | null>(null);
    isCompleted = signal<boolean>(false);

    addToMapAfterFinish = true;
    private importedTableName: string | null = null;
    private projectId: string | null = null;

    isLoading = signal(true);
    isFinishing = false;
    isAlertVisible = false;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private importWizardStorage: ImportWizardStorage,
        private importWizardPendingStorage: ImportWizardPendingStorage,
        private importWizardPendingService: ImportWizardPendingService,
        private importWizardService: ImportWizardService,
        private importWizardGeoSpatialService: ImportWizardGeoSpatialService,
        @Optional() private dialogRef: MatDialogRef<ImportWizardComponent>,
        private toastrService: ToastrService,
        private natsService: NatService,
        private mapService: MapService,
        private repositoryService: RepositoryService,
        private projectService: ProjectService,
        private mapSidebarService: MapSideBarService,
        private sharedImportsStorage: SharedImportsStorage 
    ) {}

    async ngOnInit() {
        this.projectId = await firstValueFrom(this.projectService.projectId$);
        await this.getImportWizardErrors();
        this.retrieveImportWizard();
        this.isLoading.set(false);
        await this.subscribeProgressTopic();
    }

    get hasOccurrences(): boolean {
        return this.importWizardStorage.state().hasOccurrences;
    }

    async handlePageChange(event: PageEvent) {
        this.currentPage.set(event.pageIndex + 1);
        this.pageSize.set(event.pageSize);
        await this.getImportWizardErrors();
    }

    async handleButtonClick() {
        const state = this._state();

        // Botão vira "Sair" depois de finalizado (sucesso ou erro): fecha o modal.
        if (this.isCompleted()) {
            this.handleExitClick();
            return;
        }

        this.isFinishing = true;
        this.errorMessage.set(null);
        this.successMessage.set(null);
        this.isCompleted.set(false);
        this.progressMessage.set('Iniciando importação...');
        this.progressPhase.set('upload');
        this.startProgressSimulation(0, 48);

        try { 
            const result = await this.importWizardGeoSpatialService.finishImportWizard(state.id);
            if (result?.table_name) {
                this.importedTableName = `public.${result.table_name}`;
            }
        } catch (error) {
            this.stopProgressSimulation();
            this.createRepositoryLoadingProgress$.next(0);
            this.isFinishing = false;
            this.isCompleted.set(true);
            this.errorMessage.set('Erro ao iniciar a finalização da importação. Tente novamente.');
            this.toastrService.error('Erro ao finalizar importação. Tente novamente.', '', {
                closeButton: true
            });
        }
    }

    async handleRetryClick(): Promise<void> {
        this.isCompleted.set(false);
        this.errorMessage.set(null);
        await this.handleButtonClick();
    }

    handleExitClick(): void {
        if (!this.importWizardStorage.state().isShared) {
            this.refetchRepositoryData();
            if (this.addToMapAfterFinish && this.importedTableName && this.projectId && this.hasOccurrences) {
                this.addImportedLayerToMap(this.importedTableName);
            }
        }
        if (this.dialogRef) {
            this.dialogRef.close();
        }
        if (this.importWizardStorage.state().isShared) {
            this.handleRedirectToHub();
            this.importWizardStorage.reset();
        }
    }

    private addImportedLayerToMap(tableName: string): void {
        const data: AddLayerToMapInterface = {
            project_id: this.projectId!,
            repository_id: this.repositoryService.selectedRepository().repository_id,
            table_name: tableName,
        };
        this.mapService.addLayerToMap(data).subscribe({
            next: (result) => {
                this.mapSidebarService.addLayerOnMapEvent.emit({ layer: result });
            },
            error: () => {
                this.toastrService.error(
                    'Erro ao adicionar camada ao mapa. Adicione manualmente nos repositórios.',
                    '',
                    { closeButton: true }
                );
            },
        });
    }

    // getLabelByValue(value: string): string {
    //     return this.typeOptions.find(opt => opt.value === value)!.label;
    // }

    private retrieveImportWizard() { 
        this.importWizardService.getImportWizard(this._state().id).subscribe({ 
            next: (data) => {
                this.currentImportJob.set(data)
            }, 
            error: (error) => {
                console.error(error);
            }
        });
        
    }

    handleCancelClick(): void {
        this.isAlertVisible = true;
    }

    handleCancelClickConfirmation(): void {
        this.isAlertVisible = false;
        this.importWizardStorage.handleCancelClickConfirmation(this.dialogRef);
    }

    handleCancelClickCancel(): void {
        this.isAlertVisible = false;
    }

    private async subscribeProgressTopic() {
        const topic = `import_wizard.${this._state().id}.progress`;

        this.progressSubscription = await this.natsService.subscribeToTopic(topic, this.natsCallback)
    }

    natsCallback = (msg: string) => {
        try {
            const result: NatsImportWizardProgressResponseInterface = JSON.parse(msg);

            // Erro em qualquer fase: exibe inline, marca como finalizado, mantém modal aberto.
            if (result.status === 'error') {
                this.stopProgressSimulation();
                this.isFinishing = false;
                this.isCompleted.set(true);
                this.errorMessage.set(
                    result.message || 'Falha ao processar a importação.'
                );
                return;
            }

            if (typeof result.progress_percentage === 'number') {
                // Para o progresso simulado e sincroniza com o progresso real do NATS
                this.stopProgressSimulation();

                // Nunca regride: usa o maior entre o tick falso atual e o valor real.
                const current = this.createRepositoryLoadingProgress$.getValue();
                this.createRepositoryLoadingProgress$.next(Math.max(current, result.progress_percentage));
            }
            if (result.phase) {
                const currentPhase = this.progressPhase();
                // Não permite voltar fases para evitar oscilação visual de labels (ex: rail -> upload)
                const phaseOrder = { '': 0, 'upload': 1, 'rail': 2, 'done': 3 };
                if (phaseOrder[result.phase] >= phaseOrder[currentPhase || '']) {
                    this.progressPhase.set(result.phase);
                }
            }
            // Transição upload → rail: ao receber o evento "uploaded" (upload concluído),
            // inicia imediatamente a simulação de progresso da fase 2 (50% a 94%)
            if (result.status === 'uploaded') {
                this.startProgressSimulation(50, 94);
                if (this.hasOccurrences) {
                    this.progressPhase.set('rail');
                }
            }
            if (result.message) {
                this.progressMessage.set(result.message);
            }

            // Concluído (upload + pós-processamento): mostra sucesso inline e NUNCA fecha automaticamente.
            const isDone =
                result.status === 'completed' &&
                (result.phase === 'done' || result.phase === undefined) &&
                (result.progress_percentage === undefined || result.progress_percentage === 100);

            if (isDone && !this.isCompleted()) {
                this.stopProgressSimulation();
                this.createRepositoryLoadingProgress$.next(100);
                this.isFinishing = false;
                this.isCompleted.set(true);
                this.successMessage.set(
                    result.message || 'Importação concluída com sucesso.'
                );
            }
        } catch (error) {
            console.error('Erro ao processar mensagem:', error);
        }
    }

    private handleRedirectToHub() {
        const import_link = this.sharedImportsStorage.sharedImport()?.import_link ?? '/';

        const path = import_link.startsWith('https') || import_link.startsWith('http') 
            ? new URL(import_link).pathname 
            : import_link;

        this.router.navigateByUrl(path);
    }

    private async getImportWizardErrors() { 

        const data = await firstValueFrom(this.importWizardService.getImportWizardErrors(this.importWizardStorage.state().id, this.currentPage(), this.pageSize()));

        const validationErrorsStorageData = { 
            items: data.items,
            total: data.total
        } as ValidationErrorsInterface;

        this.importWizardStorage.setValidationErrors(validationErrorsStorageData);
    }

    private refetchRepositoryData() { 

        // Refetch for GeoTables
        this.mapService.getRepositoriesGeoTablesList(this.repositoryService.selectedRepository().repository_id).subscribe(
            (success) => {
                this.repositoryService.setSpatialTables(success);
            },
        );

        // Refetch for tabular info of GeoTables
        this.mapService.getRepositoriesTablesList(this.repositoryService.selectedRepository().repository_id).subscribe(
            (success) => {
                this.repositoryService.setTabularTables(success);
            },
        );

        // Refetch for pending imports
        this.importWizardPendingService.useGetAllPendingImports(DatasetType.SPATIAL).subscribe({
            next: (data) => {
                this.importWizardPendingStorage.setPendingImports(data);
            },
            error: (error) => {
                console.error(error);
            }
        })
    }

    trackByColumnIndex(index: number, col: TabularColumns): number {
        return index;
    }

    trackByLayersIndex(index: number, lay: ImportWizardFile): number {
        return index;
    }

    async ngOnDestroy() {
        this.stopProgressSimulation();
        this.progressSubscription?.unsubscribe();
    }

    pageSizesArray = Object.values(this.pagesSizes);
    typeOptions = typeOptions;
}